/**
 * DayCell.tsx
 *
 * Renders a single day tile in the weekly tarot calendar grid.
 *
 * Each cell displays:
 * - The day number, abbreviated day name, and (optionally) the month name.
 * - A clickable card name label in the left column.
 * - The card artwork filling the right column; falls back to `TarotCardPlaceholder`
 *   when the image URL is unavailable or fails to load.
 * - Corner ornament stars whose colour reflects the voice-recognition result:
 *   - `undefined` → default gold (`text-primary/20`)
 *   - `"blue"`    → recognised and Magician applied (`text-blue-400/80`)
 *   - `"red"`     → not recognised (`text-red-400/80`)
 * - A pulsing gold border ring when the cell represents today.
 * - A thin accent gradient bar at the top when the card has been manually overridden.
 * - A hover overlay with a "Read More" label and a shimmer effect.
 *
 * Clicking either the card name or the card image calls `onCardClick` with the
 * card and date, which opens `TarotCardPopup` in the parent.
 */

'use client';

import React, { useState } from 'react';
import type { DayCellData } from './WeeklyCalendarClient';
import { useTheme } from '@/lib/themeContext';
import type { TarotCard } from '@/data/tarotData';
import TarotCardPlaceholder from './TarotCardPlaceholder';

/** Ordered day names used for the day-name label inside each cell. */
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Ordered uppercase month names used for the optional month label. */
const MONTH_NAMES = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
  'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

/** Props accepted by `DayCell`. */
interface DayCellProps {
  /** All data needed to render this day tile (date, card, flags). */
  data: DayCellData;
  /**
   * When `true`, the month name is rendered below the day name.
   * Typically only `true` for the first cell (Monday) of the visible week.
   */
  showMonthName: boolean;
  /**
   * Callback fired when the user taps the card image or card name label.
   *
   * @param card - The card currently displayed in this cell.
   * @param date - The date this cell represents.
   */
  onCardClick: (card: TarotCard, date: Date) => void;
  /**
   * Colour override for the corner ornament stars, reflecting voice-recognition state.
   * - `"blue"` — card was successfully recognised and applied. * - `"red"`  — voice input was not recognised.
   * - `undefined` — default gold colour.
   */
  starColor?: 'blue' | 'red';
}

export default function DayCell({ data, showMonthName, onCardClick, starColor }: DayCellProps) {
  const { date, card, isToday, isOverridden, isSelectedDate } = data;
  const { deckIndex } = useTheme();
  const [imageError, setImageError] = useState(false);
  const [hovered, setHovered] = useState(false);

  const dayName = DAY_NAMES[date.getDay()];
  const dayNumber = date.getDate();
  const monthName = MONTH_NAMES[date.getMonth()];

  const handleCardInteraction = () => {
    if (card) onCardClick(card, date);
  };

  // Determine corner star color class
  const starClass = starColor === 'blue' ? 'text-blue-400/80'
    : starColor === 'red' ? 'text-red-400/80' : 'text-primary/20';

  return (
    <article
      className={`
        relative rounded-xl border transition-all duration-300 overflow-hidden h-full
        ${isSelectedDate || isToday
          ? 'today-highlight border-primary/40' : 'bg-card border-border card-glow'
        }
        ${hovered ? 'card-glow-hover -translate-y-0.5' : ''}
      `}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={`${dayName} ${dayNumber} ${monthName} — ${card?.name ?? 'No card assigned'}`}
    >
      {/* Subtle corner ornaments — colored based on voice recognition result */}
      <span className={`absolute top-1.5 left-1.5 text-xs select-none transition-colors duration-500 ${starClass}`} aria-hidden>✦</span>
      <span className={`absolute top-1.5 right-1.5 text-xs select-none transition-colors duration-500 ${starClass}`} aria-hidden>✦</span>

      {/* Override indicator */}
      {isOverridden && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-accent to-transparent" />
      )}

      {/* Today glow ring */}
      {isToday && (
        <div className="absolute inset-0 rounded-xl pointer-events-none">
          <div className="absolute inset-0 rounded-xl border border-primary/30 animate-gold-pulse" />
        </div>
      )}

      {/* Cell layout: header + body filling full height */}
    <div className="absolute inset-0 p-2 sm:p-3 flex gap-2">

      {/* ── LEFT COLUMN: date info + card name ── */}
      <div className="flex-shrink-0 flex flex-col pt-4 w-[52px] sm:w-[64px]">
        <div className="flex flex-col gap-0.5">
          <span className={`font-display text-lg sm:text-2xl lg:text-3xl font-bold leading-none tabular-nums
            ${isToday ? 'text-primary' : 'text-foreground'}`}>
            {dayNumber}
          </span>
          <span className={`font-display text-[10px] sm:text-xs tracking-wider uppercase
            ${isToday ? 'text-primary/90' : 'text-muted-foreground'}`}>
            {dayName}
          </span>
          {showMonthName && (
            <span className="font-display text-[9px] sm:text-[10px] tracking-[0.2em] text-primary/80 uppercase font-semibold">
              {monthName}
            </span>
          )}
        </div>
        <div className="ornament-line mt-1 opacity-60" />

        {card && (
          <button
            onClick={handleCardInteraction}
            className="mt-1.5 font-display text-[9px] sm:text-[10px] text-left text-primary hover:text-accent transition-colors duration-200 tracking-wide leading-tight cursor-pointer hover:underline underline-offset-2 decoration-accent/50 focus:outline-none focus:ring-1 focus:ring-primary/50 rounded"
            aria-label={`View reading for ${card.name}`}
          >
            {card.name}
          </button>
        )}
      </div>

      {/* ── RIGHT COLUMN: card image filling all available space ── */}
      {card ? (
        <button
          onClick={handleCardInteraction}
          className="relative group flex-1 self-stretch focus:outline-none focus:ring-1 focus:ring-primary/40 rounded-lg overflow-hidden"
          aria-label={`Open ${card.name} card details`}
        >
          <div className="absolute inset-0 rounded-lg overflow-hidden border border-border/60
          group-hover:border-primary/50 transition-all duration-300
            group-hover:border-primary/50 transition-all duration-300
            group-hover:shadow-[0_0_16px_rgba(201,168,76,0.2)]">
            {!imageError && card.imageUrl[deckIndex] ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={card.imageUrl[deckIndex]}
                alt={`Tarot card: ${card.name}`}
                className="w-full h-full object-contain object-center tarot-card-image"
                onError={() => setImageError(true)}
              />
            ) : (
              <TarotCardPlaceholder cardName={card.name} suit={card.suit} arcana={card.arcana} />
            )}

            {/* Hover overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent
              opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-2">
              <span className="text-[8px] text-white/80 font-display tracking-widest uppercase">
                Read More
              </span>
            </div>
          </div>

          {/* Shimmer on card */}
          <div className="absolute inset-0 card-shimmer pointer-events-none rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        </button>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-center">
          <span className="text-muted-foreground/40 text-3xl">✦</span>
          <p className="font-body text-xs text-muted-foreground/50 italic">
            No card assigned
          </p>
        </div>
      )}

    </div>
    </article>
  );
}